import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { getApiUrl } from '../utils/apiConfig';
import { authHeaders, setRole, getRefresh, clearAuth, getAuthCache, setAuthCache, AppRole } from '../utils/role';
import { ensureFreshSession, refreshSession } from '../utils/authRefresh';

/**
 * AuthContext is the SERVER-AUTHORITATIVE identity for the SPA. It resolves role/tier/entitlements
 * from GET /api/auth/me (which reads the real bearer token, ignoring the backend open-access grant)
 * and exposes has(feature) for gating — the UI gates on this, never on localStorage.
 *
 * Phase 3 token model: the access token is short-lived (~2h). This provider keeps it fresh by
 * exchanging the long-lived refresh token at /api/auth/refresh — only when the access token is missing
 * or within RENEW_BEFORE_S of its exp: checked on load (a tab reopened after expiry silently recovers)
 * and every minute while the tab is open (survives laptop sleep). A valid token is never exchanged just
 * because a tab opened.
 * logout() revokes the session server-side and clears local auth.
 */
export interface AuthState {
  loading: boolean;
  authenticated: boolean;
  role: AppRole;
  tier: string;
  entitlements: string[];
  email: string;
  userId: string; // from /api/auth/me — used to attach a Paddle checkout to this account (custom_data.user_id)
  orgId: string; // from /api/auth/me — scopes per-account client caches
  // Identity stage B: account_created ≠ identity_verified ("ACCOUNT_CREATED" | "EMAIL_VERIFIED"; "" when anonymous).
  identityState: string;
  emailVerified: boolean;
  // Stage C: NOT_REQUIRED | SATISFIED | ENROLLMENT_REQUIRED | STEP_UP_REQUIRED (session-level MFA assurance).
  mfaAssurance: string;
  mfaRequired: boolean;
  mfaEnrolled: boolean;
  has: (feature: string) => boolean;
  refresh: () => void;
  logout: () => void;
}

const DEFAULT: AuthState = {
  loading: true,
  authenticated: false,
  role: 'free',
  tier: 'free',
  entitlements: [],
  email: '',
  userId: '',
  orgId: '',
  identityState: '',
  emailVerified: false,
  mfaAssurance: '',
  mfaRequired: false,
  mfaEnrolled: false,
  has: () => false,
  refresh: () => {},
  logout: () => {},
};

const AuthCtx = createContext<AuthState>(DEFAULT);

// Normalize a ProGate `feature` prop (e.g. "risk-kernel") to the backend entitlement key.
export const featureKey = (f?: string): string => (f || '').trim().toLowerCase().replace(/-/g, '_');

// How often the tab checks whether its access token is near expiry (the exchange itself happens only
// inside the renewal margin — see utils/authRefresh ensureFreshSession).
const RENEW_CHECK_MS = 60 * 1000;

// exchangeRefresh — cross-tab-safe (see utils/authRefresh): serialized refresh, re-read inside the lock,
// and a failure clears auth only for a genuinely dead session (never because another tab rotated first).
const exchangeRefresh = (): Promise<boolean> => ensureFreshSession();

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Seed from the last-known cache so a refresh renders the correct nav/gating immediately (no flash
  // of PRO locks) while /api/auth/me reconciles. A first-ever visitor has no cache → free (correct).
  const cached = getAuthCache();
  const [state, setState] = useState<Omit<AuthState, 'has' | 'refresh' | 'logout'>>({
    loading: true,
    authenticated: !!cached,
    role: cached?.role ?? 'free',
    tier: cached?.tier ?? 'free',
    entitlements: cached?.entitlements ?? [],
    email: '',
    userId: '',
    orgId: '',
    identityState: '',
    emailVerified: false,
    mfaAssurance: '',
    mfaRequired: false,
    mfaEnrolled: false,
  });
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadMe = useCallback(async (renewed = false) => {
    try {
      const res = await fetch(getApiUrl('/api/auth/me'), { headers: authHeaders() });
      if (res.ok) {
        let d = await res.json();
        // The stored plan changed after this token was issued (e.g. an upgrade in the billing portal):
        // exchange the refresh token once so the new role is in the token, then re-read.
        if (d.role_stale && !renewed) {
          await refreshSession();
          const again = await fetch(getApiUrl('/api/auth/me'), { headers: authHeaders() });
          if (again.ok) d = await again.json();
        }
        const role = (d.role || 'free') as AppRole;
        const tier = d.tier || 'free';
        const entitlements = Array.isArray(d.entitlements) ? d.entitlements : [];
        setState({ loading: false, authenticated: !!d.authenticated, role, tier, entitlements, email: d.email || '', userId: d.user_id || '', orgId: d.org_id || '', identityState: d.identity_state || '', emailVerified: !!d.email_verified, mfaAssurance: d.mfa_assurance || '', mfaRequired: !!d.mfa_required, mfaEnrolled: !!d.mfa_enrolled });
        try { setRole(role); setAuthCache({ role, tier, entitlements }); } catch { /* storage may be unavailable */ }
        return;
      }
    } catch { /* fall through to free */ }
    setState((s) => ({ ...s, loading: false }));
  }, []);

  const load = useCallback(async () => {
    // Recover/renew the access token from the refresh token first, then resolve identity.
    await exchangeRefresh();
    await loadMe();
  }, [loadMe]);

  // Explicit re-resolve (e.g. after a plan change): always exchange, so the new role is in the token.
  const forceRefresh = useCallback(async () => {
    await refreshSession();
    await loadMe(true);
  }, [loadMe]);

  const logout = useCallback(() => {
    const rt = getRefresh();
    if (rt) {
      fetch(getApiUrl('/api/auth/logout'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
      }).catch(() => {});
    }
    clearAuth();
    const base = window.location.pathname.startsWith('/saa-dashboard') ? '/saa-dashboard' : '';
    window.location.assign(base + '/login');
  }, []);

  useEffect(() => {
    load();
    // Keep the access token fresh while the tab stays open (access TTL ~2h).
    timer.current = setInterval(() => { exchangeRefresh(); }, RENEW_CHECK_MS);
    // A gated call whose session could not be refreshed (utils/authFetch) → re-resolve identity so the UI
    // shows the sign-in state instead of silently failing with plan errors.
    const onExpired = () => { loadMe(); };
    window.addEventListener('saa-auth-expired', onExpired);
    return () => { if (timer.current) clearInterval(timer.current); window.removeEventListener('saa-auth-expired', onExpired); };
  }, [load, loadMe]);

  const has = useCallback(
    (feature: string) => state.entitlements.includes(featureKey(feature)),
    [state.entitlements],
  );

  return <AuthCtx.Provider value={{ ...state, has, refresh: forceRefresh, logout }}>{children}</AuthCtx.Provider>;
}

export const useAuth = (): AuthState => useContext(AuthCtx);
